import { gql } from '@apollo/client';

const cableViewItem = gql`
  fragment CableViewItem on CableView {
    id
    fromEquipmentTag
    toEquipmentTag
    numberOfInsertBlocks
    cutLength
    hasPackages
    hasReels
    hasCableNotes
    hasTerms
    hasVias
    numberOfInsertBlocks
    hasCableToTermNotes
    hasCableFromTermNotes
  }
`;

export const cableFragments = {
  cableItem: gql`
    ${cableViewItem}
    fragment CableItem on Cable {
      id
      tag
      cableCodeId
      cableCode {
        id
        tag
      }
      cableView {
        ...CableViewItem
      }
      cableFromEnds {
        equipmentId
        equipment {
          tag
        }
      }
      cableToEnds {
        equipmentId
        equipment {
          tag
        }
      }
      drawing
      drawing2
      cableRevision
      connectionDrawing
      onelineDrawing
      schematicDrawing
      primaryStatus
      engineeringStatus
      secondaryStatus
      constructionStatus
      holdStatus
      cardRev
      numberOfCables
      bundleCableCodes
      preliminaryLength
      maxAmpacityLength
      designLength
      engrLength
      actualLength
      addlServiceLevel {
        id
        name
      }
      addlServiceLevelId
      disciplineCode
      safetyCode
      unit
      facility
      isMeggerRequired
      meggerDevice
      serviceLevelId
      serviceLevel {
        id
        name
      }
      startupSystemId
      startupSystem {
        id
        tag
      }
      systemId
      system {
        id
        tag
      }
      separationGroupId
      separationGroup {
        id
        name
      }
      routeEngrStatus
      fromTermDrawing
      fromTermDrawing2
      fromTailLength
      fromTermGland
      toTermDrawing
      toTermDrawing2
      toTailLength
      toTermGland
      ownerId
      misc1
      misc2
      misc3
      misc4
      misc5
      misc6
      misc7
      misc8
      misc9
      misc10
      misc11
      misc12
      misc13
      misc14
      misc15
      misc16
      misc17
      misc18
      misc19
      misc20
      miscDate1
      miscDate2
      installedTime
      installedBy
      fromTermPrimaryStatus
      toTermPrimaryStatus
      installedByUser {
        id
        nameFirst
        nameLast
        nameMiddle
      }
      inspectedTime
      inspectedBy
      inspectedByUser {
        id
        nameFirst
        nameLast
        nameMiddle
      }
      preparedTime
      preparedBy
      preparedByUser {
        id
        nameFirst
        nameLast
        nameMiddle
        userAccountPhoto {
          id
          photo
        }
      }
      checkedTime
      checkedBy
      checkedByUser {
        id
        nameFirst
        nameLast
        nameMiddle
        userAccountPhoto {
          id
          photo
        }
      }
      approvedTime
      approvedBy
      approvedByUser {
        id
        nameFirst
        nameLast
        nameMiddle
        userAccountPhoto {
          id
          photo
        }
      }
      updatedTime
      updatedBy
      updatedByUser {
        id
        nameFirst
        nameLast
        nameMiddle
        userAccountPhoto {
          id
          photo
        }
      }

      isRestricted
    }
  `,
  feedbackCableItem: gql`
    fragment FeedbackCableFields on Cable {
      id
      actualLength
      constructionStatus
      installedTime
      installedBy
      inspectedTime
      inspectedBy
      meggerDevice
      tag
      engrLength
      updatedBy
      updatedTime
      cableCode {
        id
        tag
      }
      updatedByUser {
        nameFirst
        nameLast
        nameMiddle
        userAccountPhoto {
          id
          photo
        }
      }
      updatedBy
      updatedTime
    }
  `,
  feedbackCableReelItem: gql`
    fragment FeedbackCableReelFields on CableReel {
      id
      assignedLength
      cutLength
      reelId
      cableId
      sequenceNumber
      reel {
        tag
        id
        badLength
        totalLength
        usedLength
      }
    }
  `,
  feedbackTerminationItem: gql`
    fragment FeedbackTerminationFields on Cable {
      id
      tag
      maxConnections
      numberOfCables
      fromTermConstStatus
      fromTermConnMade
      fromTermCrimpTool
      fromTermInstalledBy
      fromTermInstalledTime
      fromTermInspectedBy
      fromTermInspectedTime
      fromTermUpdatedTime
      fromTermUpdatedBy
      fromTermUpdatedByUser {
        nameFirst
        nameLast
        nameMiddle
        userAccountPhoto {
          id
          photo
        }
      }
      toTermConstStatus
      toTermConnMade
      toTermCrimpTool
      toTermInstalledBy
      toTermInstalledTime
      toTermInspectedBy
      toTermInspectedTime
      toTermUpdatedTime
      toTermUpdatedBy
      toTermUpdatedByUser {
        nameFirst
        nameLast
        nameMiddle
        userAccountPhoto {
          id
          photo
        }
      }
      updatedBy
      updatedTime
      cableCode {
        id
        tag
        numberOfConductors
        numberOfShields
        numberOfGrounds
      }
      updatedByUser {
        nameFirst
        nameLast
        nameMiddle
        userAccountPhoto {
          id
          photo
        }
      }
      cableMarks {
        id
        sequenceNumber
        cableEnd
        mark
      }
      cableView {
        fromEquipmentTag
        toEquipmentTag
      }
    }
  `,
};

export const CREATE_CABLE = gql`
  ${cableFragments.cableItem}
  mutation CreateCable($createCableInput: CreateCableInput!) {
    createCable(createCableInput: $createCableInput) {
      cable {
        ...CableItem
      }
      errors
      infos
    }
  }
`;

export const DELETE_CABLE = gql`
  mutation DeleteCable($deleteCableInput: DeleteCableInput!) {
    deleteCable(deleteCableInput: $deleteCableInput) {
      id
      errors
      infos
    }
  }
`;

export const UPDATE_CABLE = gql`
  ${cableFragments.cableItem}
  mutation UpdateCable($updateCableInput: UpdateCableInput!) {
    updateCable(updateCableInput: $updateCableInput) {
      cable {
        ...CableItem
      }
      errors
      infos
    }
  }
`;

export const UPDATE_CABLES = gql`
  ${cableFragments.cableItem}
  mutation UpdateCables($updateCablesInput: [UpdateCableInput!]!) {
    updateCables(updateCablesInput: $updateCablesInput) {
      cables {
        ...CableItem
      }
      infos {
        tag
        messageKeys
      }
      errors {
        tag
        messageKeys
      }
    }
  }
`;

export const GET_CABLE_BY_ID = gql`
  ${cableFragments.cableItem}
  query CableById($id: ID!) {
    cableById(id: $id) {
      ...CableItem
    }
  }
`;

export const GET_CABLE_BY_IDS = gql`
  ${cableFragments.cableItem}
  query CableByIds($ids: [ID!]!) {
    cableByIds(ids: $ids) {
      ...CableItem
    }
  }
`;

export const ISSUE_CABLES = gql`
  ${cableFragments.cableItem}
  mutation IssueCables($issueCablesInput: [IssueCableInput!]!) {
    issueCables(issueCablesInput: $issueCablesInput) {
      cables {
        ...CableItem
      }
      errors {
        messageKeys
        tag
      }
    }
  }
`;
export const SEARCH_CABLE = gql`
  ${cableFragments.cableItem}
  query SearchCables($searchInput: SearchInput) {
    searchCables(searchInput: $searchInput) {
      items {
        ...CableItem
      }
    }
  }
`;

export const SEARCH_CABLE_FROM_TO_TERMS = gql`
  ${cableFragments.feedbackTerminationItem}
  query SearchCables($searchInput: SearchInput) {
    searchCables(searchInput: $searchInput) {
      items {
        ...FeedbackTerminationFields
      }
    }
  }
`;

export const FEEDBACK_TERMINATION = gql`
  ${cableFragments.feedbackTerminationItem}
  mutation FeedbackTermination($feedbackTerminationInput: FeedbackTerminationInput!) {
    feedbackTermination(feedbackTerminationInput: $feedbackTerminationInput) {
      cable {
        ...FeedbackTerminationFields
      }
      errors
    }
  }
`;

export const FEEDBACK_CABLE = gql`
  ${cableFragments.feedbackCableItem}
  ${cableFragments.feedbackCableReelItem}

  mutation FeedbackCable($feedbackCableInput: FeedbackCableInput!) {
    feedbackCable(feedbackCableInput: $feedbackCableInput) {
      cable {
        ...FeedbackCableFields
      }
      cableReels {
        ...FeedbackCableReelFields
      }
      errors
      infos
    }
  }
`;

export const GET_CABLE_STATS = gql`
  query CableStats($cableStatsInput: CableStatsInput) {
    cableStats(cableStatsInput: $cableStatsInput) {
      totalCount
      revisedCardCount
      routedCount
      terminatedCount
      onHoldCount
      preliminaryCount
      releasedCount
      jobsiteCount
      constructedCount
    }
  }
`;

export const SEARCH_CABLE_REELS = gql`
  ${cableFragments.feedbackCableReelItem}
  query SearchCableReels($searchInput: SearchInput) {
    searchCableReels(searchInput: $searchInput) {
      items {
        ...FeedbackCableReelFields
      }
    }
  }
`;

export const GET_CABLE_VIEW_STATS = gql`
  ${cableViewItem}
  query GetCableViewStats($id: ID!) {
    cableById(id: $id) {
      id
      cableView {
        ...CableViewItem
      }
    }
  }
`;

export const GET_CABLE_BY_TAG = gql`
  query SearchCables($searchInput: SearchInput) {
    searchCables(searchInput: $searchInput) {
      items {
        id
      }
    }
  }
`;
